<template>
    <div class="col-md-12 mx-auto">
        <div class="card">
            <div class="card-header card-header bg-primary text-white h5 m-0">
                <page-title :title="($t('account.title'))"></page-title>
            </div>
        </div>

        <div v-if="showError">
            <div class="alert alert-danger">{{ $t(errorMessage) }}</div>
        </div>

        <kendo-tabstrip @activate="onTabActivate" ref="tabs">
            <ul>
                <li class="k-state-active" id="personTab">{{ $t('person.title') }}</li>
                <li id="addressTab" v-show="hasUser">{{ $t('person.livingAddress') }}</li>
                <li id="bankAccountTab" v-show="hasUser">{{ $t('company.bankAccount') }}</li>
                <li id="activeFormsTab" v-show="hasUser">{{ $t('registers.forms.formToEvent.title') }}</li>
                <li id="participantsTab">{{ $t('person.participation') }}</li>
                <li id="certificatesTab" v-show="hasUser && claims.certificates.read">
                    {{ $t('person.certificates') }}
                </li>
                <li id="changePasswordTab" v-show="isCurrent">{{ $t('person.changePassword') }}</li>
                <li id="tasksTab" v-show="hasUser && claims.tasks.read">{{ $t('person.tasks') }}</li>
                <li id="claimsTab" v-show="hasUser && claims.claims.read">{{ $t('claims.title') }}</li>
                <li id="filesTab" v-show="hasUser">{{ $t('documents') }}</li>
                <li id="moodleAccountDetailsTab" v-show="hasUser">{{ $t('person.moodleAccountDetails') }}</li>
                <li id="policyTab" v-show="hasUser && userRoles.includes('DevAdmin')">{{ $tc('policy.title', 2) }}</li>
                <li id="clientCertificateTab" v-show="isCurrent">{{ $t('clientCertificate.title') }}</li>
            </ul>
            <div>
                <div v-if="personTabActive">
                    <div v-if="userModel != null">
                        <user-details-card v-model="userModel"
                                           :allowEdit="false">
                        </user-details-card>
                    </div>

                    <PersonManagement
                        :userId="loggedUserId"
                        :details="isDetails"
                        :personId="personId"
                        :showPersonHistoryData="showPersonHistoryData"
                    />
                </div>
            </div>
            <div v-show="hasUser">
                <div v-if="addressTabActive">
                    <contact-address :loadCurrentUserAddress="true"
                                     :allowEdit="isCurrent"
                                     :isAddressDetails="true"
                                     :street-required="false"
                                     :userId="person.userId">
                    </contact-address>
                </div>
            </div>
            <div v-show="hasUser">
                <div v-if="bankAccountActive">
                    <bank-account :allowEdit="isCurrent"
                                  :is-bank-account-details="true"
                                  :userId="person.userId"
                                  :bank-account="person.bankAccount">
                    </bank-account>
                </div>
            </div>
            <div v-show="hasUser">
                <div v-if="activeFormsTabActive">
                    <div v-if="isCurrent">
                        <temp-ranking-pending-confirmation class="mt-2"
                                                           :userId="person.userId"></temp-ranking-pending-confirmation>
                    </div>

                    <div v-if="isCurrent">
                        <active-forms-grid class="mb-2"
                                           :showPinButton="false"
                                           :showNewButton="false"
                                           :showResizeIcon="false"
                                           :showSettingsBtn="false"
                                           :returnUrl="'/account'"
                                           :userId="person.userId">
                        </active-forms-grid>
                    </div>

                    <my-forms-grid :showPinButton="false"
                                   :showNewButton="false"
                                   :showResizeIcon="false"
                                   :showSettingsBtn="false"
                                   :returnUrl="'/account'"
                                   :eventId="eventId"
                                   :userId="person.userId">
                    </my-forms-grid>
                </div>
            </div>
            <div>
                <div v-if="participantsTabActive">
                    <participants-grid ref="participantsGrid"
                                       :showHideButton="false"
                                       :showPinButton="false"
                                       :showNewButton="false"
                                       :showResizeIcon="false"
                                       :showSettingsBtn="false"
                                       :showBtns="true"
                                       :listAllKind="true"
                                       :forLoggedUser="isCurrent"
                                       :componentName="'MyParticipationsListGrid'"
                                       :userId="person.userId"
                                       :personId="person.id ? Number(person.id) : 0">
                    </participants-grid>
                </div>
            </div>
            <div v-show="hasUser && claims.certificates.read">
                <div v-if="certificatesTabActive">
                    <user-certificates-list
                        :userId="person.userId"
                        :personId="personId"
                    />
                </div>
            </div>
            <div v-show="isCurrent">
                <div v-if="changePasswordTabActive">
                    <changePassword :editable="isCurrent" :allowAnonymous="false"></changePassword>
                </div>
            </div>
            <div v-show="hasUser">
                <div v-if="tasksTabActive && claims.tasks.read">
                    <tasks :editable="isCurrent" :userId="person.userId"></tasks>
                </div>
            </div>
            <div v-show="hasUser && claims.claims.read">
                <div v-if="claimsTabActive">
                    <UserClaimsDetails :userId="person.userId"
                                       :showCurrentUserLoadedClaims="isCurrent"></UserClaimsDetails>
                </div>
            </div>
            <div v-show="hasUser">
                <div v-if="filesTabActive">
                    <personal-files :user-id="person.userId"
                                    :allow-edit="isCurrent">
                    </personal-files>
                </div>
            </div>
            <div v-show="hasUser">
                <div v-if="moodleAccountDetailsTabActive">
                    <moodle-account-details v-model="userModel">
                    </moodle-account-details>
                </div>
            </div>
            <div v-show="hasUser && userRoles.includes('DevAdmin')">
                <div v-if="policyTabActive">
                    <h6>{{ userPolicies }}</h6>
                </div>
            </div>
            <div v-show="isCurrent">
                <div v-if="clientCertificateTabActive">
                    <client-certificate-details
                        :user-id="person.userId"
                    />
                    <div
                        v-if="!certAttachDisabled"
                        class="text-center"
                    >
                        <a :href="certAttachUrl" @click="certAttachDisabled=true" class="btn btn-primary mx-0">{{$t('buttons.add.btnText')}}</a>
                    </div>
                </div>
            </div>
        </kendo-tabstrip>
    </div>
</template>

<script>
    import Vue from 'vue';
    import PageTitle from '@/components/common/PageTitle';
    import ActiveFormsGrid from '@/components/forms/ActiveFormsGrid';
    import MyFormsGrid from '@/components/forms/CompletedFormsGrid';
    import ParticipantsGrid from '@/components/participants/ParticipantsGrid';
    import UserDetailsCard from '@/components/user/UserDetailsCard'
    import ContactAddress from '@/components/address/Address';
    import { LayoutInstaller } from '@progress/kendo-layout-vue-wrapper';
    import ChangePassword from '@/components/person/ChangePassword';
    import TempRankingPendingConfirmation from '@/components/dynamic/ranking/TempRankingPendingConfirmation';
    import { AspNetUser, Person } from '@/models/models';
    import Tasks from '@/components/account/Tasks';
    import UserClaimsDetails from '@/components/administration/UserClaimsDetails';
    import BankAccount from "@/components/bankAccount/BankAccount";
    import PersonalFiles from "@/components/account/PersonalFiles";
    import MoodleAccountDetails from "@/components/account/MoodleAccountDetails"
    import PersonManagement from '@/components/account/PersonManagement';
    import UserCertificatesList from '@/components/account/UserCertificatesList';
    import actionLogService from "@/services/actionLog.service"
    import userService from '@/services/user.service';
    import ClientCertificateDetails from '@/components/user/ClientCertificateDetails.vue';
    import { mapGetters } from 'vuex';

    Vue.use(LayoutInstaller);

    export default {
        inject: ['$validator'],
        props: {
            eventId: {
                type: String,
                default: ''
            },
        },
        components: {
            PageTitle,
            ActiveFormsGrid,
            MyFormsGrid,
            ContactAddress,
            ParticipantsGrid,
            ChangePassword,
            TempRankingPendingConfirmation,
            UserDetailsCard,
            PersonManagement,
            Tasks,
            UserClaimsDetails,
            UserCertificatesList,
            BankAccount,
            PersonalFiles,
            MoodleAccountDetails,
            ClientCertificateDetails
        },
        data() {
            return {
                person: null,
                userModel: null,
                personTabActive: true,
                addressTabActive: false,
                activeFormsTabActive: false,
                participantsTabActive: false,
                changePasswordTabActive: false,
                bankAccountActive: false,
                filesTabActive: false,
                tasksTabActive: false,
                claimsTabActive: false,
                certificatesTabActive: false,
                showPersonHistoryData: true,
                moodleAccountDetailsTabActive: false,
                policyTabActive: false,
                clientCertificateTabActive: false,
                certAttachDisabled: false,
                showError: false,
                errorMessage: ''
            };
        },
        methods: {
            onTabActivate(e) {
                const tabId = e.item.id;
                if (!tabId) {
                    return;
                }

                switch (tabId) {
                    case 'personTab':
                        this.resetAllTabs();
                        this.personTabActive = true;
                        break;
                    case 'addressTab':
                        this.resetAllTabs();
                        this.addressTabActive = true;
                        break;
                    case 'bankAccountTab': {
                        this.resetAllTabs();
                        this.bankAccountActive = true;
                        break;
                    }
                    case 'activeFormsTab':
                        this.resetAllTabs();
                        this.activeFormsTabActive = true;
                        break;
                    case 'participantsTab':
                        this.resetAllTabs();
                        this.participantsTabActive = true;
                        break;
                    case 'changePasswordTab':
                        this.resetAllTabs();
                        this.changePasswordTabActive = true;
                        break;
                    case 'tasksTab':
                        this.resetAllTabs();
                        this.tasksTabActive = true;
                        break;
                    case 'claimsTab':
                        this.resetAllTabs();
                        this.claimsTabActive = true;
                        break;
                    case 'certificatesTab':
                        this.resetAllTabs();
                        this.certificatesTabActive = true;
                        break;
                    case 'filesTab':
                        this.resetAllTabs();
                        this.filesTabActive = true;
                        break;
                    case 'moodleAccountDetailsTab': {
                        this.resetAllTabs();
                        this.moodleAccountDetailsTabActive = true;
                        break;
                    }
                    case 'policyTab':
                        this.resetAllTabs();
                        this.policyTabActive = true;
                        break;
                    case 'clientCertificateTab':
                        this.resetAllTabs();
                        this.clientCertificateTabActive = true;
                        break;
                    default:
                        break;
                }
            },
            resetAllTabs() {
                this.personTabActive = false;
                this.addressTabActive = false;
                this.activeFormsTabActive = false;
                this.participantsTabActive = false;
                this.changePasswordTabActive = false;
                this.tasksTabActive = false;
                this.claimsTabActive = false;
                this.bankAccountActive = false;
                this.filesTabActive = false;
                this.policyTabActive = false;
                this.clientCertificateTabActive = false;
            },
            checkIfPersonIsUser() {
                this.$http.get('/api/person/GetUseId/' + this.person.id).then(result => {
                    this.person.userId = result.data;
                    this.loadUser();
                });
            },
            loadUser() {
                if (!this.person || !this.person.userId) {
                    return;
                }

                userService
                    .getById(this.person.userId)
                    .then(response => {
                        let model = response.data;
                        if (model) {
                            this.userModel = new AspNetUser(model);
                        } else {
                            this.userModel = null;
                        }
                    })
                    .catch(error => {
                        this.userModel = null;
                    });
            },
        },
        created: function () {
            const id = this.$route.params.id;

            if (id) {
                actionLogService.personDetailsReview(id);

                if (!this.claims.participant.read) {
                    this.$router.push('/home');
                } else {
                    this.person = new Person({ id });
                    this.checkIfPersonIsUser();
                }
            } else {
                this.person = new Person({ userId: this.$store.getters.userId });
                this.loadUser();
            }
        },
        mounted() {
            if (this.$route.query.tab) {
                this.$refs.tabs.kendoWidget().select('#' + this.$route.query.tab);
            }

            if (this.$route.query.error) {
                this.showError = true;
                this.errorMessage = this.$route.query.errorMessage;
            }
        },
        computed: {
            ...mapGetters(['claims', 'userId', 'userRoles', 'userPolicies', 'authToken']),
            isCurrent() {
                if (!this.person || !this.person.userId || !this.userId) {
                    return false;
                }

                return this.person.userId === this.userId;
            },
            hasUser() {
                const hasUser = (this.person && this.person.userId) || this.isCurrent;
                return hasUser;
            },
            loggedUserId() {
                return this.userId;
            },
            personId() {
                return Number(this.person.id);
            },
            isDetails() {
                let personId = this.personId;

                return !isNaN(personId) && personId > 0;
            },
            certAttachUrl() {
                return `${this.$store.getters.baseUrl}/certificate/attach?userId=${this.person.userId}&access_token=${this.authToken}`;
            }
        }
    };
</script>

<style scoped>
</style>
